import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Edit, Trash2, Plus, Eye, UserPlus, Upload, FileText } from 'lucide-react';
import { adminApi } from '../services/api';
import { Fair } from '../types';
import { showError } from '../utils/toast';
import { extractApiError } from '../utils/errorUtils';
import { ROUTES } from '../constants/routes';
import FairImportModal from './FairImportModal';

export default function AdminDashboard() {
  const [fairs, setFairs] = useState<Fair[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<number | null>(null);
  const [showImportModal, setShowImportModal] = useState(false);

  useEffect(() => {
    loadFairs();
  }, []);

  const loadFairs = async () => {
    try {
      setLoading(true);
      // Use admin endpoint to get all artist fairs including empty ones
      const data = await adminApi.getAllFairs();
      setFairs(data);
      setError(null);
    } catch (err: unknown) {
      const apiError = extractApiError(err);
      console.error('Failed to load artist fairs:', apiError);
      setError(apiError.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await adminApi.deleteFair(id);
      setFairs(fairs.filter(fs => fs.id !== id));
      setDeleteConfirm(null);
    } catch (err: unknown) {
      const apiError = extractApiError(err);
      console.error('Failed to delete artist fair:', apiError);
      showError('Failed to delete artist fair', apiError.message || 'Please try again or contact support if the problem persists');
    }
  };

  // Format next date for display (Issue #82)
  const formatNextDate = (nextDate: string | null | undefined): string => {
    if (!nextDate) return 'TBD';

    const date = new Date(nextDate);
    const now = new Date();
    const diffMs = date.getTime() - now.getTime();
    const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));

    if (diffDays < 0) return 'Past';
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Tomorrow';
    if (diffDays <= 7) return `${diffDays} days`;

    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  if (loading) {
    return (
      <div className="text-center py-8" data-testid="admin-dashboard-loading">
        <p className="text-gray-500">Loading artist fairs...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-8" data-testid="admin-dashboard-error">
        <p className="text-red-500" data-testid="admin-error-message">{error}</p>
        <button
          onClick={loadFairs}
          data-testid="retry-load-button"
          className="mt-4 px-4 py-2 bg-primary text-white rounded hover:bg-primary-dark"
        >
          Try Again
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-3 w-full max-w-full overflow-hidden" data-testid="admin-dashboard">
      {/* Header */}
      <div className="bg-white shadow-sm rounded-lg p-3">
        <div className="flex flex-col gap-2">
          <h1 className="text-base sm:text-xl font-bold text-gray-900" data-testid="admin-dashboard-title">Artist Fairs</h1>
          <div className="flex flex-col sm:flex-row gap-2">
            <Link
              to="/admin/fairs/new"
              aria-label="Add new artist fair"
              data-testid="add-fair-button"
              className="flex items-center justify-center gap-2 px-3 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark text-sm"
            >
              <Plus className="h-4 w-4 flex-shrink-0" />
              <span>Add New</span>
            </Link>
            <button
              onClick={() => setShowImportModal(true)}
              aria-label="Import fairs from CSV or Excel"
              data-testid="import-fairs-button"
              className="flex items-center justify-center gap-2 px-3 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 text-sm"
            >
              <Upload className="h-4 w-4 flex-shrink-0" />
              <span>Import Fairs</span>
            </button>
            <Link
              to={ROUTES.ADMIN.PRELAUNCH_SIGNUPS}
              aria-label="Manage pre-launch signups"
              data-testid="prelaunch-signups-button"
              className="flex items-center justify-center gap-2 px-3 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 text-sm"
            >
              <UserPlus className="h-4 w-4 flex-shrink-0" />
              <span>Pre-Launch Signups</span>
            </Link>
            <Link
              to="/admin/manager-applications"
              aria-label="Review fair manager applications"
              data-testid="manager-applications-button"
              className="flex items-center justify-center gap-2 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm"
            >
              <FileText className="h-4 w-4 flex-shrink-0" />
              <span>Manager Applications</span>
            </Link>
          </div>
        </div>
      </div>

      {/* Desktop Table View (hidden on mobile) */}
      <div className="hidden lg:block bg-white shadow-sm rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full table-fixed divide-y divide-gray-200" data-testid="admin-fairs-table">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" style={{width: '20%'}}>
                  Artist Fair
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" style={{width: '12%'}}>
                  Location
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" style={{width: '12%'}}>
                  Next Date
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" style={{width: '10%'}}>
                  Slots
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" style={{width: '8%'}}>
                  Vendors
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" style={{width: '18%'}}>
                  Contact
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" style={{width: '20%'}}>
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {fairs.map((fair) => (
                <tr key={fair.id} data-testid="admin-fair-row">
                  <td className="px-4 py-4">
                    <div className="min-w-0">
                      <div className="text-sm font-medium text-gray-900 truncate">
                        {fair.name}
                      </div>
                      <div className="text-sm text-gray-500 truncate">
                        {fair.description ? fair.description.substring(0, 40) + '...' : 'No description'}
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-4">
                    <div className="text-sm text-gray-900 truncate">
                      {fair.address.city}, {fair.address.state}
                    </div>
                    <div className="text-sm text-gray-500">
                      {fair.address.zip}
                    </div>
                  </td>
                  <td className="px-4 py-4">
                    <div className="text-sm text-gray-900">
                      {formatNextDate(fair.next_date)}
                    </div>
                  </td>
                  <td className="px-4 py-4 text-center">
                    <div className="text-sm text-gray-900">
                      {fair.available_slots ?? 0} / {fair.total_slots ?? 0}
                    </div>
                  </td>
                  <td className="px-4 py-4 text-center">
                    <div className="text-sm text-gray-900">
                      {fair.vendor_count ?? 0}
                    </div>
                  </td>
                  <td className="px-4 py-4">
                    <div className="text-sm text-gray-900 truncate">
                      {fair.email}
                    </div>
                    {fair.phone && (
                      <div className="text-sm text-gray-500 truncate">
                        {fair.phone}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-4 text-right text-sm font-medium">
                    <div className="flex items-center justify-end space-x-1">
                      <Link
                        to={`/fair/${fair.id}`}
                        data-testid="view-fair-link"
                        className="text-secondary hover:text-teal-900 p-1"
                        target="_blank"
                        title="View"
                      >
                        <Eye className="h-4 w-4" />
                      </Link>
                      <Link
                        to={`/admin/fairs/${fair.id}/edit`}
                        data-testid="edit-fair-link"
                        className="text-secondary hover:text-ink p-1"
                        title="Edit"
                      >
                        <Edit className="h-4 w-4" />
                      </Link>
                      <button
                        onClick={() => setDeleteConfirm(fair.id)}
                        data-testid="delete-fair-button"
                        className="text-red-600 hover:text-red-900 p-1"
                        title="Delete"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                </td>
              </tr>
            ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Mobile Card View (hidden on desktop) */}
      <div className="lg:hidden space-y-3">
        {fairs.map((fair) => (
          <div key={fair.id} className="bg-white shadow-sm rounded-lg p-3 space-y-2" data-testid="admin-fair-card">
            {/* Header with name */}
            <div className="min-w-0 mb-2">
              <h3 className="text-sm font-medium text-gray-900 truncate leading-tight">
                {fair.name}
              </h3>
              <p className="text-xs text-gray-500 truncate leading-tight">
                {fair.description ? fair.description.substring(0, 40) + '...' : 'No description'}
              </p>
            </div>

            {/* Details */}
            <div className="grid grid-cols-2 gap-2 text-sm min-w-0">
              <div className="min-w-0">
                <span className="text-gray-500 block text-xs">Location</span>
                <span className="text-gray-900 font-medium text-xs block truncate">
                  {fair.address.city}, {fair.address.state}
                </span>
              </div>
              <div className="min-w-0">
                <span className="text-gray-500 block text-xs">Next Date</span>
                <span className="text-gray-900 font-medium text-xs">
                  {formatNextDate(fair.next_date)}
                </span>
              </div>
              <div className="min-w-0">
                <span className="text-gray-500 block text-xs">Slots</span>
                <span className="text-gray-900 font-medium text-xs">
                  {fair.available_slots ?? 0} / {fair.total_slots ?? 0}
                </span>
              </div>
              <div className="min-w-0">
                <span className="text-gray-500 block text-xs">Vendors</span>
                <span className="text-gray-900 font-medium text-xs">
                  {fair.vendor_count ?? 0}
                </span>
              </div>
              <div className="col-span-2 min-w-0">
                <span className="text-gray-500 block text-xs">Contact</span>
                <span className="text-gray-900 font-medium text-xs block truncate">{fair.email}</span>
                {fair.phone && (
                  <span className="text-gray-600 text-xs block truncate">{fair.phone}</span>
                )}
              </div>
            </div>

            {/* Actions */}
            <div className="flex items-center justify-between pt-2 border-t border-gray-100">
              <Link
                to={`/fair/${fair.id}`}
                data-testid="view-fair-link"
                className="flex items-center justify-center flex-1 py-1.5 text-xs text-secondary hover:bg-secondary-light/50 rounded"
                target="_blank"
              >
                <Eye className="h-4 w-4" />
              </Link>
              <Link
                to={`/admin/fairs/${fair.id}/edit`}
                data-testid="edit-fair-link"
                className="flex items-center justify-center flex-1 py-1.5 text-xs text-secondary hover:bg-moonbone rounded"
              >
                <Edit className="h-4 w-4" />
              </Link>
              <button
                onClick={() => setDeleteConfirm(fair.id)}
                data-testid="delete-fair-button"
                className="flex items-center justify-center flex-1 py-1.5 text-xs text-red-600 hover:bg-red-50 rounded"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          </div>
        ))}
      </div>

      {fairs.length === 0 && (
        <div className="text-center py-12" data-testid="admin-no-fairs">
          <p className="text-gray-500 mb-4">No artist fairs found</p>
          <Link
            to="/admin/fairs/new"
            data-testid="add-first-fair-button"
            className="inline-flex items-center space-x-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark"
          >
            <Plus className="h-4 w-4" />
            <span>Add Your First Artist Fair</span>
          </Link>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full flex items-center justify-center z-50 p-4" data-testid="delete-fair-modal">
          <div className="bg-white p-4 sm:p-6 rounded-lg shadow-lg max-w-md w-full">
            <h3 className="text-lg font-medium text-gray-900 mb-4" data-testid="delete-modal-title">
              Confirm Delete
            </h3>
            <p className="text-sm sm:text-base text-gray-600 mb-6" data-testid="delete-modal-message">
              Are you sure you want to delete this artist fair? This action cannot be undone.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
              <button
                onClick={() => handleDelete(deleteConfirm)}
                data-testid="admin-dashboard-confirm-delete-button"
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
              >
                Delete
              </button>
              <button
                onClick={() => setDeleteConfirm(null)}
                data-testid="admin-dashboard-cancel-delete-button"
                className="flex-1 px-4 py-2 bg-gray-300 text-gray-700 rounded hover:bg-gray-400"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Fair Import Modal (Issue #1431) */}
      <FairImportModal
        isOpen={showImportModal}
        onClose={() => setShowImportModal(false)}
        onSuccess={() => {
          loadFairs(); // Reload fairs list after successful import
        }}
      />
    </div>
  );
}